import { jsPDF } from "jspdf";
import {
  TEXT_X_MM, TEXT_WIDTH_MM,
  mmToPt, drawHeader, drawFooter, getTextRegion, getSignatureLineY,
  addPageWithHeader, drawPatientIdentityRows,
} from "./prescriptionLayout";
import { fmtDatePdf } from "@emr/shared/india";
import type { TaperStep } from "@emr/shared";
import { groupTaperSteps } from "@emr/shared";

// 2026-11-01, Batch 7 Wave 2 dedupe — drawPatientIdentityRows and drawMixedLine
// were extracted verbatim to ./layoutShared.ts (re-exported through this
// renderer's layout sibling, prescriptionLayout) — they were byte-identical
// (modulo the export keyword) with the copies inlined in visitSummaryPdf.ts /
// investigationPdf.ts / dischargeSummaryPdf.ts. computeAgeYm / formatAgeLabel
// moved with drawPatientIdentityRows (their only uses here). drawPatientInfoBlock
// and splitDiagnosisItems stay local (single carrier). The former public exports
// are preserved unchanged for importers:
export { drawMixedLine, drawPatientIdentityRows } from "./prescriptionLayout";

// ── Table ruling (2026-10-26; unified same day) ────────────────────────────
// One scheme shared by the diagnosis table and the medicine table: ALL table
// ruling — horizontal row dividers, outer frames, AND vertical column
// separators — prints at 0.5pt warm gray (150,138,128). The original 0.3pt
// #E1DAD2 ruling was effectively invisible on paper; an interim two-weight
// scheme (subtle 0.3pt verticals) was unified to this single visible weight
// per user request.
const RULE_COLOR: [number, number, number] = [150, 138, 128];
const RULE_WIDTH = 0.5;

export interface PrescriptionMedicine {
  serialNo: number;
  name: string;
  composition: string | null;
  strength: string | null;
  frequency: string | null;
  taper: TaperStep[] | null;
  duration: string | null;
  instructions: string | null;
}

export interface PrescriptionPdfData {
  patientName: string;
  clinicUhid: string;
  patientDob: string | null;
  patientGender: string | null;
  checkInAt: string;
  diagnosis: string | null;
  medicines: PrescriptionMedicine[];
  // When false (default), only the drug NAME prints — no strength suffix and
  // no composition. When true, composition prints in parentheses.
  showComposition: boolean;
  reviewNeeded: boolean;
  reviewDate: string | null;
  doctorName: string | null;
  doctorQualifications: string | null;
  doctorRegNumber: string | null;
}

/**
 * Patient info block drawn directly below the letterhead. Callers must
 * call drawHeader/drawFooter first; returns the Y for content below it
 * (one blank row below the block).
 *
 * Blank row · drawPatientIdentityRows (Name/Date, Age+Sex/UHID rows) ·
 * bold "Diagnosis" heading on the left · numbered diagnoses below it,
 * split across two columns sequentially (items 1…k down the left column,
 * k+1…n down the right) with the split point k chosen by rendered content
 * height so the two columns come out roughly equal; each column's row
 * heights are independent (a multi-line item never stretches the other
 * column's rows).
 */
export async function drawPatientInfoBlock(
  doc: jsPDF,
  data: {
    patientName: string;
    clinicUhid: string;
    patientDob: string | null;
    patientGender: string | null;
    checkInAt: string;
    diagnosis: string | null;
  },
  startY?: number,
  opts?: { region?: { x: number; width: number }; diagnosisFontSize?: number },
): Promise<number> {
  const r = opts?.region ?? getTextRegion(doc);
  const x = r.x;
  const w = r.width;
  // 1.5 line spacing for the diagnosis text (baseline-to-baseline).
  const diagSize = opts?.diagnosisFontSize ?? 12;
  const lineH = diagSize * 1.5;

  // One blank row below the letterhead, then the identity rows.
  let y = drawPatientIdentityRows(doc, data, (startY ?? getTextRegion(doc).y) + mmToPt(5), r);

  // Row 3 — bold "Diagnosis" heading on the left.
  doc.setFont("helvetica", "bold");
  doc.setFontSize(diagSize);
  doc.setTextColor(26, 20, 19);
  doc.text("Diagnosis", x, y);
  y += lineH;

  // Numbered diagnoses in two columns, below the heading. Drawn in a subtle
  // table (light gray borders, same as the prescription table) with a
  // vertical separator between the two columns and horizontal row
  // separators between each diagnosis row. The table is inset 5mm
  // further from the left and right than the prescription table.
  const items = splitDiagnosisItems(data.diagnosis);
  if (items.length > 0) {
    // Single diagnosis item → merge the two columns into one full-width
    // column so no empty right column is drawn. Two or more items use the
    // two-column layout, filled SEQUENTIALLY: items 1…k down the left
    // column top-to-bottom, items k+1…n down the right. The split point k
    // is chosen by rendered CONTENT HEIGHT, not item count: every item is
    // measured (wrapped line count at the column wrap width) and k
    // minimizes |leftHeight − rightHeight| so the two columns come out as
    // equal as the content allows. Each column then lays out its own rows
    // with heights derived only from that column's items — a multi-line
    // item never stretches the other column's rows.
    const singleCol = items.length === 1;
    const diagInset = mmToPt(5);
    const dx = x + diagInset;
    const dw = w - 2 * diagInset;
    const colW = singleCol ? dw : dw / 2;
    // Text inset from each column's left line — 2mm (reduced from 4mm per
    // request to tighten the gap between the column margin and the text).
    // Wrap width reserves this 2mm on the left plus a 2mm right margin so
    // text clears the column separator.
    const diagTextInset = mmToPt(2);
    const diagTextWrap = colW - diagTextInset - mmToPt(2);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(diagSize);
    doc.setTextColor(60, 50, 45);

    // Compact row layout — 1.5mm top pad and 1.5mm bottom pad around the
    // text (measured from the rendered PDF). diagBase is the text baseline
    // from the row top; the single-line row height is diagBase + bottom
    // extra. Multi-line rows add diagLineH per extra line.
    const diagBase = mmToPt(4.2); // text baseline from row top → 1.5mm top pad
    const diagLineH = lineH; // baseline-to-baseline for wrapped lines

    // Measure every item once, with its final label. Sequential fill keeps
    // numbering stable across split choices: item i (0-based) always prints
    // as "${i + 1}. ", whichever column it lands in.
    const itemLines = items.map((it, i) => doc.splitTextToSize(`${i + 1}. ${it}`, diagTextWrap) as string[]);
    const itemH = itemLines.map((lines) => diagBase + diagLineH * (Math.max(lines.length, 1) - 1) + mmToPt(2.1));

    // Choose the split point k (first k items → left column) minimizing the
    // height difference between the columns. Clamped to [1, n−1] so neither
    // column is ever empty when there are two or more items.
    let split = 1;
    if (!singleCol) {
      const total = itemH.reduce((s, h) => s + h, 0);
      let best = Infinity;
      let prefix = 0;
      for (let k = 1; k < items.length; k++) {
        prefix += itemH[k - 1];
        const diff = Math.abs(prefix - (total - prefix));
        if (diff < best) {
          best = diff;
          split = k;
        }
      }
    }

    const tableTop = y;
    // Column bottoms are computable upfront (all item heights are pre-
    // measured), so the ruling pass can run after ALL content with a
    // controlled z-order: zebra fills → text → vertical separator (subtle)
    // → horizontal row separators + outer frame (visible) — the visible
    // horizontals sit on top at line crossings.
    const leftCount = singleCol ? items.length : split;
    const rightCount = singleCol ? 0 : items.length - split;
    const colBottom = (firstIdx: number, count: number): number =>
      tableTop + itemH.slice(firstIdx, firstIdx + count).reduce((s, h) => s + h, 0);
    const leftBottom = colBottom(0, leftCount);
    const rightBottom = rightCount > 0 ? colBottom(split, rightCount) : tableTop;
    const tableBottom = Math.max(leftBottom, rightBottom);

    // Content pass — one column's stack of rows at colX starting at item
    // index firstIdx, heights from that column's items only. Zebra striping
    // (odd rows cream 252,248,243 — mirrors the drugs table) is PER-COLUMN:
    // the two columns' row boundaries rarely align, so each column stripes
    // itself.
    const drawColumn = (colX: number, firstIdx: number, count: number): void => {
      let rowY = tableTop;
      for (let i = 0; i < count; i++) {
        const idx = firstIdx + i;
        if (i % 2 === 1) {
          doc.setFillColor(252, 248, 243);
          doc.rect(colX, rowY, colW, itemH[idx], "F");
        }
        if (itemLines[idx].length > 0) {
          doc.text(itemLines[idx], colX + diagTextInset, rowY + diagBase);
        }
        rowY += itemH[idx];
      }
    };
    drawColumn(dx, 0, leftCount);
    if (rightCount > 0) drawColumn(dx + colW, split, rightCount);

    // Ruling pass — single visible weight everywhere. Vertical separator
    // between the two columns (only when there are two columns; with a
    // single diagnosis item the table is one full-width column, so no
    // separator is drawn — avoids a blank right column), then per-column
    // horizontal row separators (the columns' row boundaries rarely align,
    // so each column rules itself), then the outer frame. Not at the very
    // top or bottom inside a column — the outer frame handles those edges.
    doc.setDrawColor(...RULE_COLOR);
    doc.setLineWidth(RULE_WIDTH);
    if (!singleCol) {
      doc.line(dx + colW, tableTop, dx + colW, tableBottom);
    }
    const drawColSeps = (colX: number, firstIdx: number, count: number): void => {
      let sepY = tableTop;
      for (let i = 0; i < count - 1; i++) {
        sepY += itemH[firstIdx + i];
        doc.line(colX, sepY, colX + colW, sepY);
      }
    };
    drawColSeps(dx, 0, leftCount);
    if (rightCount > 0) drawColSeps(dx + colW, split, rightCount);
    // Outer frame — completes the table's clean print ruling.
    doc.rect(dx, tableTop, dw, tableBottom - tableTop);

    y = tableBottom;
  } else {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(diagSize);
    doc.setTextColor(60, 50, 45);
    doc.text("1. —", x, y);
    y += lineH;
  }

  // One blank row between the diagnosis listing and the prescription table.
  return y + mmToPt(5);
}

/**
 * Split the diagnosis textarea into numbered print items. Each non-empty
 * line is an item; an explicit "1." / "1)" / "1:" prefix typed by the user is
 * dropped (the PDF applies its own numbering).
 */
function splitDiagnosisItems(diagnosis: string | null): string[] {
  if (!diagnosis) return [];
  return diagnosis
    .split(/\r?\n/)
    .map((line) => line.trim().replace(/^\d+[.)\]:]\s*/, ""))
    .filter((line) => line.length > 0);
}

export async function createPrescriptionPdf(data: PrescriptionPdfData): Promise<jsPDF> {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(12);

  await drawHeader(doc);
  await drawFooter(doc);

  // Prescription body: left inset 10mm (5mm base + 5mm extra shift right),
  // right inset 5mm. Text starts 20mm from the left page edge (10mm page
  // margin + 10mm left inset) and 15mm from the right page edge.
  const baseRegion = getTextRegion(doc);
  const leftInset = mmToPt(10);
  const rightInset = mmToPt(5);
  const x = baseRegion.x + leftInset;
  const w = baseRegion.width - leftInset - rightInset;
  const rxRegion = { x, width: w };

  let y = await drawPatientInfoBlock(doc, data, undefined, { region: rxRegion, diagnosisFontSize: 11 });

  // 5-column table: No., Drug, Instructions, Frequency, Days.
  // 11pt text, double-spaced rows (8.5mm pitch per line). The Instructions
  // column uses 10pt (smaller, to fit more text); all other columns use 11pt.
  const TABLE_FONT = 11;
  const INSTR_FONT = 10;
  // Composition prints below the drug name at 8pt (smaller than the 11pt
  // name and 10pt instructions) so it reads as a secondary line.
  const COMP_FONT = 8;
  // Composition lines sit one full row pitch (lineH) below the last drug-
  // name baseline — same spacing as a wrapped name line, so there is no
  // overlap and the composition reads as a clean subtitle. Subsequent
  // composition wrap lines are COMP_LINE_H apart (8pt pitch). A bottom
  // pad equal to `base` mirrors the top padding so the row has the same
  // air above the name and below the composition.
  const COMP_LINE_H = mmToPt(4.5);
  // "No." column — wide enough for 2 digits at 11pt (~2 number widths).
  const colSr = mmToPt(8);
  const colDuration = mmToPt(22);
  // Left pad inside every cell — plain AND taper rows share the same pad
  // so their instructions/frequency/days columns align exactly.
  const cellPad = mmToPt(2);
  const lineH = mmToPt(8.5); // double spacing for 11pt rows
  // Gap between the last drug-name baseline and the first composition line.
  // 4mm — the composition sits 4mm below the drug name as a subtitle.
  const COMP_GAP = mmToPt(4);
  // Drug is the remainder after the fixed columns; 15% of it is given to
  // Instructions (so a wider Drug column shrinks, a narrower one grows the
  // Instructions column).
  const colFreq = mmToPt(38.5);
  const colDrugBase = w - colSr - colFreq - colDuration - mmToPt(31.5);
  const drugShareToInstr = colDrugBase * 0.15;
  const colDrug = colDrugBase - drugShareToInstr;
  const colInstr = mmToPt(31.5) + drugShareToInstr;

  // Convert a decimal dose string to an ASCII vulgar fraction (e.g. 1.5 ->
  // "1 1/2", 0.5 -> "1/2", 2.25 -> "2 1/4"). Non-numeric or whole-number
  // strings pass through unchanged so "PRN", "1", "SOS", "1-0-1" segments
  // etc. are not affected.
  //
  // 2026-10-02 ROOT-CAUSE FIX (see SCHEMA.md §18): the previous version used
  // the Unicode single-character fractions ½ ¼ ¾ (U+00BD/BC/BE) to save
  // space. That was the root cause of the frequency column bleeding into
  // the Days column: jsPDF's standard-font metrics table has NO entry for
  // those glyphs, so getTextWidth/splitTextToSize measured them at ~530/1000
  // em while every PDF viewer's substituted Helvetica actually RENDERS them
  // at ~556/1000 em (measured 9.17pt vs jsPDF's 5.83pt at 11pt — a 57%
  // understatement, PyMuPDF ground-truth 2026-10-02). Wrapping and
  // centering therefore computed against a width that does not match what
  // is drawn, packing too many segments per line and overshooting the
  // column boundary (18.54pt past the Freq|Days separator on the real
  // SYNDOPA PLUS row). ASCII "n/d" uses only core WinAnsi glyphs whose
  // metrics ARE exact in every viewer, so measurement == rendering and
  // splitTextToSize wraps correctly. Same lesson as the U+211E Rx-glyph
  // incident (§18 2026-09-19): with jsPDF's built-in fonts, only emit
  // glyphs whose widths are exactly measured.
  const decimalToFraction = (s: string): string => {
    const n = Number(s);
    if (!Number.isFinite(n) || !s.includes(".")) return s;
    const sign = n < 0 ? "-" : "";
    const abs = Math.abs(n);
    const whole = Math.floor(abs);
    const decimal = abs - whole;
    // ASCII "n/d" form for all supported clinical fractions. 1/2, 1/4, 3/4
    // are here too (they WERE Unicode glyphs before the 2026-10-02 fix).
    const asciiFrac: [number, string][] = [
      [0.5, "1/2"],
      [0.25, "1/4"],
      [0.75, "3/4"],
      [0.125, "1/8"],
      [0.375, "3/8"],
      [0.625, "5/8"],
      [0.875, "7/8"],
      [0.333333, "1/3"],
      [0.666667, "2/3"],
      [0.2, "1/5"],
      [0.4, "2/5"],
      [0.6, "3/5"],
      [0.8, "4/5"],
    ];
    for (const [val, label] of asciiFrac) {
      if (Math.abs(decimal - val) < 0.001) {
        if (whole === 0) return `${sign}${label}`;
        return `${sign}${whole} ${label}`;
      }
    }
    // Fall back to the original string if no clean fraction found.
    return s;
  };

  // Format frequency for legibility: "1-1-1" -> "1 - 1 - 1".
  // Decimal doses are converted to fractions: 1.5 -> "1 1/2", 0.5 -> "1/2",
  // 0.25 -> "1/4", 0.75 -> "3/4", 2.5 -> "2 1/2", etc.
  const fmtFreq = (f: string | null | undefined): string => {
    if (!f) return "";
    return f
      .split(/(\s*-\s*)/)
      .map((seg) => {
        if (/^\s*-\s*$/.test(seg)) return " - ";
        const trimmed = seg.trim();
        if (!trimmed) return seg;
        const frac = decimalToFraction(trimmed);
        return frac;
      })
      .join("");
  };

  // Column boundaries for the vertical separators (computed once, used on
  // every page the table spans).
  const colBoundaries = [
    x + cellPad + colSr,
    x + cellPad + colSr + colDrug,
    x + cellPad + colSr + colDrug + colInstr,
    x + cellPad + colSr + colDrug + colInstr + colFreq,
  ];
  // Center X for the Instructions and Frequency columns — used by both
  // drawTableHeader (header labels) and the row-drawing code (cell text).
  // Declared BEFORE drawTableHeader is called to avoid the temporal dead
  // zone (const bindings are block-scoped and not initialized until their
  // declaration line — referencing them earlier throws ReferenceError at
  // runtime, which TS does not catch).
  const instrCenterX = x + cellPad + colSr + colDrug + colInstr / 2;
  const freqCenterX = x + cellPad + colSr + colDrug + colInstr + colFreq / 2;

  // Header row — text positions mirror the row text positions (2mm right
  // of each column boundary so they clear the vertical separators).
  // Extracted into a function so it can be repeated at the top of each
  // continuation page when the table flows across pages.
  const drawTableHeader = (headerY: number): number => {
    doc.setFillColor(247, 240, 232);
    doc.rect(x, headerY, w, mmToPt(8), "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(TABLE_FONT);
    doc.setTextColor(124, 45, 18);
    // Column text x-positions (2mm right of each column boundary) for the
    // left-aligned columns; Instructions and Frequency are centered using
    // instrCenterX / freqCenterX computed above.
    const hSr = x + cellPad;
    const hDrug = x + cellPad + colSr + cellPad;
    const hDays = x + cellPad + colSr + colDrug + colInstr + colFreq;
    doc.text("No.", hSr, headerY + mmToPt(5.5));
    doc.text("Drug", hDrug, headerY + mmToPt(5.5));
    doc.text("Instructions", instrCenterX, headerY + mmToPt(5.5), { align: "center" });
    doc.text("Frequency", freqCenterX, headerY + mmToPt(5.5), { align: "center" });
    // Days column — left-aligned 2mm from the Freq|Days separator (was
    // centered, which left ~11mm of empty space before short values like
    // "5" or "10 days"). cellPad = 2mm, matching the other left-aligned
    // columns (No., Drug).
    doc.text("Days", hDays + cellPad, headerY + mmToPt(5.5));
    doc.setFont("helvetica", "normal");
    doc.setTextColor(26, 20, 19);
    doc.setFontSize(TABLE_FONT);
    return headerY + mmToPt(8);
  };

  // Draw the header on the first page.
  const tableTop = y;
  y = drawTableHeader(y);
  // Tracks the top of the table on the current page (for drawing vertical
  // separators per page). Updated on page breaks inside drawMedicineRow.
  const pageTopRef = { val: tableTop };

  // Row boundary Ys (header bottom + bottom of each medicine row) for drawing
  // subtle horizontal row separators. Reset on each page break so Ys stay
  // page-relative (jsPDF Y is per-page, not document-absolute).
  let rowBoundaries: number[] = [y]; // header bottom = top of first row

  // Draw wrapped lines at the table's double-spaced pitch (lineH per line),
  // starting at startY. Returns nothing — callers track y themselves.
  const drawWrapped = (lines: string[], lx: number, startY: number) => {
    for (let i = 0; i < lines.length; i++) {
      doc.text(lines[i], lx, startY + lineH * i);
    }
  };

  // Draw composition lines (8pt, no parentheses) below a drug name. The
  // composition's first baseline sits COMP_GAP below the LAST drug-name
  // line baseline (passed as lastNameBaselineY), and subsequent composition
  // lines are spaced COMP_LINE_H apart. Returns the number of lines drawn.
  // `comp` is the composition text; `showComp` gates whether anything is
  // drawn. The font is restored to TABLE_FONT afterwards.
  const drawCompLines = (comp: string | null, showComp: boolean, lx: number, lastNameBaselineY: number): number => {
    if (!showComp || !comp || !comp.trim()) return 0;
    doc.setFontSize(COMP_FONT);
    const lines = doc.splitTextToSize(comp.trim(), colDrug - cellPad - mmToPt(1)) as string[];
    const startY = lastNameBaselineY + COMP_GAP;
    for (let i = 0; i < lines.length; i++) {
      doc.text(lines[i], lx, startY + COMP_LINE_H * i);
    }
    doc.setFontSize(TABLE_FONT);
    return lines.length;
  };

  // Draw ALL table ruling for the current page segment from pageTop to
  // endY at the single visible weight (0.5pt warm gray 150,138,128) —
  // vertical column separators, the outer frame, and horizontal row
  // separators. (The original 0.3pt #E1DAD2 ruling was effectively
  // invisible on paper; an interim two-weight scheme with subtle verticals
  // was unified to this single weight per user request.) Horizontal lines
  // are drawn at each rowBoundary Y that falls within this page's table
  // segment; they are drawn last (after all zebra fills) so they sit on top
  // and remain visible.
  const drawSeparators = (pageTop: number, endY: number) => {
    doc.setDrawColor(...RULE_COLOR);
    doc.setLineWidth(RULE_WIDTH);
    for (const bx of colBoundaries) {
      doc.line(bx, pageTop, bx, endY);
    }
    // Outer rect outline around this page's table segment.
    doc.rect(x, pageTop, w, endY - pageTop);
    // Horizontal row separators — lines between every row.
    for (const hy of rowBoundaries) {
      if (hy > pageTop + 0.5 && hy < endY - 0.5) {
        doc.line(x, hy, x + w, hy);
      }
    }
  };

  // Page-break handler: when the table flows to a new page, draw separators
  // for the page that just ended, add the new page (header + footer via
  // ensureSpace), draw the table header again, and return the new Y + new
  // pageTop for separator tracking. The new page's frame starts at the
  // header band TOP (pageStart) — matching page 1, where tableTop is
  // captured before drawTableHeader — so the header band is framed and the
  // vertical separators cross it on every page the table spans.
  const onPageBreak = async (prevPageTop: number, endY: number): Promise<{ y: number; pageTop: number }> => {
    drawSeparators(prevPageTop, endY);
    const pageStart = await addPageWithHeader(doc);
    const headerBottom = drawTableHeader(pageStart);
    // Reset row boundaries for the new page (header bottom = top of first row).
    rowBoundaries = [headerBottom];
    return { y: headerBottom, pageTop: pageStart };
  };

  // Draw one medicine row: main line (drug name, optional composition) plus
  // indented protocol (taper) rows below, one line per step. Plain and taper
  // rows share identical column X positions (cellPad + column offsets), so
  // instructions/frequency/days align across both row kinds.
  // pageTopRef tracks the top of the table on the current page (for separator
  // drawing); it is updated when a page break occurs mid-row.
  const drawMedicineRow = async (m: PrescriptionMedicine, rowIdx: number, startY: number, pageTopRef: { val: number }): Promise<number> => {
    let y = startY;
    const base = mmToPt(6); // text baseline inside an 8.5mm-pitch line
    // Taper step lines use a tighter baseline (4mm) and pitch (7mm) so the
    // date/freq/days rows sit closer to the drug name header above them,
    // reducing the visual gap between the header and the first step.
    const stepBase = mmToPt(4);
    const stepLineH = mmToPt(7);
    const cxSr = x + cellPad;
    // Drug text starts 2mm to the right of its column boundary (cellPad)
    // so it clears the vertical separators. Instructions and Frequency are
    // centered within their columns (instrCenterX / freqCenterX computed
    // above), so they don't need a left-pad X position.
    const cxDrug = x + cellPad + colSr + cellPad;
    // Days column's left edge (the Freq|Days boundary). Text is drawn
    // left-aligned at + cellPad (2mm) from this separator — was centered
    // (colDuration/2 ≈ 11mm) which left a wide gap for short values.
    const cxDays = x + cellPad + colSr + colDrug + colInstr + colFreq;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(TABLE_FONT);

    // Main drug line: name only. Composition (when showComposition is on) prints
    // on its own line below the name at COMP_FONT (8pt), no parentheses.
    // Wrap width = colDrug minus the 2mm left pad (cellPad) and a 1mm right
    // margin so text clears the separator.
    const drugLines = doc.splitTextToSize(m.name, colDrug - cellPad - mmToPt(1)) as string[];
    // Measure composition at COMP_FONT (8pt) — splitTextToSize uses the
    // current font size, so we must set it to 8pt before wrapping. If the
    // font is left at TABLE_FONT (11pt), longer compositions (e.g. 40 chars)
    // wrap to 2 lines at 11pt even though they fit on 1 line at 8pt — the
    // block height would then reserve space for 2 lines while drawCompLines
    // draws only 1 (at 8pt), leaving an empty gap.
    doc.setFontSize(COMP_FONT);
    const compLines = (data.showComposition && m.composition && m.composition.trim())
      ? doc.splitTextToSize(m.composition.trim(), colDrug - cellPad - mmToPt(1)) as string[]
      : [];
    doc.setFontSize(TABLE_FONT);

    // Group taper steps by drug name (per-strength protocol). When all steps
    // share the row name (legacy / whole-protocol-one-strength), this yields
    // a single group and the output is identical to the old single-header
    // behaviour. When steps carry per-step drug overrides, consecutive
    // same-name steps are grouped under one header line each.
    const groups = m.taper && m.taper.length > 0 ? groupTaperSteps(m.taper, m.name) : null;
    const multiGroup = !!(groups && groups.length > 1);

    // Plain rows: instructions may wrap — the row height must cover the
    // taller of drug name / instructions. Instructions wrap at 10pt.
    // Wrap width = colInstr minus the 2mm left pad (cellPad) and a 1mm
    // right margin so text clears the Instr|Freq separator.
    let instrLines: string[];
    if ((!m.taper || m.taper.length === 0) && m.instructions) {
      doc.setFontSize(INSTR_FONT);
      instrLines = doc.splitTextToSize(m.instructions, colInstr - cellPad - mmToPt(1)) as string[];
      doc.setFontSize(TABLE_FONT);
    } else {
      instrLines = ["" as string];
    }

    // Wrap frequency to the column width so it doesn't overflow into the
    // Instructions or Days columns. fmtFreq adds spaces around hyphens for
    // legibility; splitTextToSize wraps at those spaces. The row height
    // (mainH below) accounts for the wrapped line count.
    const freqStr = (!m.taper || m.taper.length === 0) ? fmtFreq(m.frequency) : "";
    const freqLines: string[] = freqStr
      ? doc.splitTextToSize(freqStr, colFreq - cellPad - mmToPt(1)) as string[]
      : ["" as string];

    // Block height for the name + composition area. The name's first baseline
    // is `base` from the row top. The composition's first baseline sits
    // COMP_GAP (4mm) below the last name baseline — a subtitle gap.
    // Subsequent composition wrap lines are COMP_LINE_H apart. A bottom pad
    // of `base` mirrors the top padding so the row has the same air above the
    // name and below the composition. The row must also be at least one
    // lineH tall (freq/days on the first line) and at least as tall as the
    // wrapped instructions (at lineH pitch).
    const compBottomPad = mmToPt(2);
    const nameBlockH = compLines.length > 0
      ? base + lineH * (drugLines.length - 1) + COMP_GAP + COMP_LINE_H * (compLines.length - 1) + compBottomPad
      : lineH;
    const mainH = Math.max(nameBlockH, lineH * Math.max(instrLines.length, freqLines.length, 1));

    // Total block height incl. protocol steps (one line per step, plus
    // wrapping for step instructions). Step instructions wrap at 10pt.
    // In multi-group mode, each group also adds a header line (drug name).
    let blockH = mainH;
    if (groups) {
      if (multiGroup) {
        // Multi-group: each group draws a header (drug name + optional
        // composition line at 8pt) + its step lines. The first group's
        // header reuses mainH (it carries the sr.no); subsequent groups
        // add their own header height.
        blockH = 0;
        for (let gi = 0; gi < groups.length; gi++) {
          const g = groups[gi];
          const gComp = (g.steps.find((s) => s.composition && s.composition.trim())?.composition ?? "").trim() || null;
          const gCompCount = (data.showComposition && gComp)
            ? (doc.setFontSize(COMP_FONT), doc.splitTextToSize(gComp, colDrug - cellPad - mmToPt(1)) as string[]).length
            : 0;
          if (doc.getFontSize() !== TABLE_FONT) doc.setFontSize(TABLE_FONT);
          const gLinesCount = doc.splitTextToSize(g.name, colDrug - cellPad - mmToPt(1)).length;
          const headerH = gCompCount > 0
            ? base + lineH * (gLinesCount - 1) + COMP_GAP + COMP_LINE_H * (gCompCount - 1) + mmToPt(2)
            : Math.max(lineH * gLinesCount, lineH);
          blockH += (gi === 0 ? Math.max(headerH, mainH) : headerH);
          for (const step of g.steps) {
            let sInstr: string[];
            if (step.instructions) {
              doc.setFontSize(INSTR_FONT);
              sInstr = doc.splitTextToSize(step.instructions, colInstr - cellPad - mmToPt(1)) as string[];
              doc.setFontSize(TABLE_FONT);
            } else {
              sInstr = ["" as string];
            }
            const sFreqCount = (doc.splitTextToSize(fmtFreq(step.frequency), colFreq - cellPad - mmToPt(1)) as string[]).length;
            blockH += stepLineH * Math.max(sInstr.length, sFreqCount, 1);
          }
        }
      } else {
        // Single group: mainH covers the drug name + composition line, steps add below.
        for (const step of groups[0].steps) {
          let sInstr: string[];
          if (step.instructions) {
            doc.setFontSize(INSTR_FONT);
            sInstr = doc.splitTextToSize(step.instructions, colInstr - cellPad - mmToPt(1)) as string[];
            doc.setFontSize(TABLE_FONT);
          } else {
            sInstr = ["" as string];
          }
          const sFreqCount = (doc.splitTextToSize(fmtFreq(step.frequency), colFreq - cellPad - mmToPt(1)) as string[]).length;
          blockH += stepLineH * Math.max(sInstr.length, sFreqCount, 1);
        }
      }
    }

    // If the whole row block doesn't fit on the current page, draw
    // separators for the page that's ending, then start a new page with
    // a fresh table header. This prevents a row from being split across
    // pages — the entire block (drug name + instructions + taper steps)
    // moves to the next page as a unit.
    const region = getTextRegion(doc);
    const bottomLimit = region.y + region.maxHeight;
    if (y + blockH > bottomLimit) {
      const { y: newY, pageTop } = await onPageBreak(pageTopRef.val, y);
      y = newY;
      pageTopRef.val = pageTop;
    }
    if (rowIdx % 2 === 1) {
      doc.setFillColor(252, 248, 243);
      doc.rect(x, y, w, blockH, "F");
    }

    doc.setTextColor(26, 20, 19);
    doc.text(String(m.serialNo), cxSr, y + base);

    // Instructions / frequency / days for a plain medicine.
    if (!m.taper || m.taper.length === 0) {
      // Drug name on the first line of the block (normal weight, not bold).
      doc.text(drugLines[0], cxDrug, y + base);
      if (drugLines.length > 1) {
        drawWrapped(drugLines.slice(1), cxDrug, y + base + lineH);
      }
      // Composition on its own line(s) below the drug name, 8pt, no parens.
      if (compLines.length > 0) {
        drawCompLines(m.composition, data.showComposition, cxDrug, y + base + lineH * (drugLines.length - 1));
      }
      if (m.instructions) {
        doc.setFontSize(INSTR_FONT);
        for (let i = 0; i < instrLines.length; i++) {
          doc.text(instrLines[i], instrCenterX, y + base + lineH * i, { align: "center" });
        }
        doc.setFontSize(TABLE_FONT);
      }
      // Frequency — wrapped to the column width, centered per line.
      if (freqLines.length > 0) {
        for (let i = 0; i < freqLines.length; i++) {
          doc.text(freqLines[i], freqCenterX, y + base + lineH * i, { align: "center" });
        }
      }
      // Days column — left-aligned 2mm from the Freq|Days separator.
      doc.text(m.duration ?? "", cxDays + cellPad, y + base);
      y += mainH;
      return y;
    }

    if (!groups) { y += mainH; return y; }

    // Taper protocol. Two render paths:
    //  - multiGroup: each group draws its own drug-name header line (with the
    //    sr.no on the first group only), then the group's date/freq/days
    //    lines indented one tab. This is the per-strength protocol layout.
    //  - single group (legacy): the row-level drug name on the main line,
    //    then all steps indented one tab — unchanged from the old layout.
    const tabIndent = mmToPt(6);
    if (multiGroup) {
      for (let gi = 0; gi < groups.length; gi++) {
        const g = groups[gi];
        // Group header: drug name (the name carries the strength); the sr.no
        // is printed only on the first group's header line (already drawn).
        const gName = g.name;
        const gLines = doc.splitTextToSize(gName, colDrug - cellPad - mmToPt(1)) as string[];
        const gComp = (g.steps.find((s) => s.composition && s.composition.trim())?.composition ?? "").trim() || null;
        const gCompCount = (data.showComposition && gComp)
          ? (doc.setFontSize(COMP_FONT), (doc.splitTextToSize(gComp, colDrug - cellPad - mmToPt(1)) as string[]).length)
          : 0;
        if (doc.getFontSize() !== TABLE_FONT) doc.setFontSize(TABLE_FONT);
        const gHeaderH = gCompCount > 0
          ? base + lineH * (gLines.length - 1) + COMP_GAP + COMP_LINE_H * (gCompCount - 1) + mmToPt(2)
          : Math.max(lineH * gLines.length, lineH);
        if (gi === 0) {
          // First group: the sr.no was already drawn at y + base; draw the
          // drug name on the same line, then composition below it.
          doc.text(gLines[0], cxDrug, y + base);
          if (gLines.length > 1) {
            drawWrapped(gLines.slice(1), cxDrug, y + base + lineH);
          }
          if (gCompCount > 0) {
            drawCompLines(gComp, data.showComposition, cxDrug, y + base + lineH * (gLines.length - 1));
          }
          y += Math.max(gHeaderH, mainH);
        } else {
          // Subsequent group: new header line (no sr.no). The header height
          // is the name lines (lineH each) + the composition block (one
          // COMP_GAP below the last name baseline + COMP_LINE_H per wrap
          // line + base bottom pad) when composition is shown.
          const gCompCount = (data.showComposition && gComp)
            ? (doc.setFontSize(COMP_FONT), (doc.splitTextToSize(gComp, colDrug - cellPad - mmToPt(1)) as string[]).length)
            : 0;
          if (doc.getFontSize() !== TABLE_FONT) doc.setFontSize(TABLE_FONT);
          const headerH = gCompCount > 0
            ? base + lineH * (gLines.length - 1) + COMP_GAP + COMP_LINE_H * (gCompCount - 1) + mmToPt(2)
            : Math.max(lineH * gLines.length, lineH);
          if (y + headerH > bottomLimit) {
            const { y: newY, pageTop } = await onPageBreak(pageTopRef.val, y);
            y = newY;
            pageTopRef.val = pageTop;
          }
          doc.text(gLines[0], cxDrug, y + base);
          if (gLines.length > 1) {
            drawWrapped(gLines.slice(1), cxDrug, y + base + lineH);
          }
          if (gCompCount > 0) {
            drawCompLines(gComp, data.showComposition, cxDrug, y + base + lineH * (gLines.length - 1));
          }
          y += headerH;
        }
        // Steps in this group: date range indented, freq/days/instr in columns.
        for (const step of g.steps) {
          const [fy, fmo, fd] = step.fromDate.split("-").map(Number);
          const toIso = new Date(Date.UTC(fy, fmo - 1, fd + step.days - 1)).toISOString().slice(0, 10);
          const rangeText = step.days === 1
            ? fmtDatePdf(step.fromDate)
            : `${fmtDatePdf(step.fromDate)} – ${fmtDatePdf(toIso)}`;
          let sInstr: string[];
          if (step.instructions) {
            doc.setFontSize(INSTR_FONT);
            sInstr = doc.splitTextToSize(step.instructions, colInstr - cellPad - mmToPt(1)) as string[];
          } else {
            sInstr = ["" as string];
          }
          const sFreqLines = doc.splitTextToSize(fmtFreq(step.frequency), colFreq - cellPad - mmToPt(1)) as string[];
          const stepH = stepLineH * Math.max(sInstr.length, sFreqLines.length, 1);
          if (y + stepH > bottomLimit) {
            const { y: newY, pageTop } = await onPageBreak(pageTopRef.val, y);
            y = newY;
            pageTopRef.val = pageTop;
          }
          doc.setTextColor(60, 50, 45);
          doc.text(rangeText, cxDrug + tabIndent, y + stepBase);
          if (step.instructions) {
            for (let i = 0; i < sInstr.length; i++) {
              doc.text(sInstr[i], instrCenterX, y + stepBase + stepLineH * i, { align: "center" });
            }
          }
          doc.setFontSize(TABLE_FONT);
          doc.setTextColor(26, 20, 19);
          for (let i = 0; i < sFreqLines.length; i++) {
            doc.text(sFreqLines[i], freqCenterX, y + stepBase + stepLineH * i, { align: "center" });
          }
          doc.text(`${step.days} days`, cxDays + cellPad, y + stepBase);
          doc.setTextColor(26, 20, 19);
          y += stepH;
        }
      }
      return y;
    }

    // Single-group taper (legacy / whole-protocol-one-strength):
    // drug name on the main line, all steps indented one tab below it.
    doc.text(drugLines[0], cxDrug, y + base);
    if (drugLines.length > 1) {
      drawWrapped(drugLines.slice(1), cxDrug, y + base + lineH);
    }
    // Composition on its own line below the drug name, 8pt, no parens.
    if (compLines.length > 0) {
      drawCompLines(m.composition, data.showComposition, cxDrug, y + base + lineH * (drugLines.length - 1));
    }
    y += mainH;
    for (const step of groups[0].steps) {
      // Pure UTC math: To = From + Days − 1 (never local-time Date, which
      // shifts a day on UTC+offset machines — postmortem 2026-09-20).
      const [fy, fmo, fd] = step.fromDate.split("-").map(Number);
      const toIso = new Date(Date.UTC(fy, fmo - 1, fd + step.days - 1)).toISOString().slice(0, 10);
      const rangeText = step.days === 1
        ? fmtDatePdf(step.fromDate)
        : `${fmtDatePdf(step.fromDate)} – ${fmtDatePdf(toIso)}`;
      let sInstr: string[];
      if (step.instructions) {
        doc.setFontSize(INSTR_FONT);
        sInstr = doc.splitTextToSize(step.instructions, colInstr - cellPad - mmToPt(1)) as string[];
      } else {
        sInstr = ["" as string];
      }
      const sFreqLines = doc.splitTextToSize(fmtFreq(step.frequency), colFreq - cellPad - mmToPt(1)) as string[];
      const stepH = stepLineH * Math.max(sInstr.length, sFreqLines.length, 1);
      if (y + stepH > bottomLimit) {
        const { y: newY, pageTop } = await onPageBreak(pageTopRef.val, y);
        y = newY;
        pageTopRef.val = pageTop;
      }
      doc.setTextColor(60, 50, 45);
      doc.text(rangeText, cxDrug + tabIndent, y + stepBase);
      if (step.instructions) {
        for (let i = 0; i < sInstr.length; i++) {
          doc.text(sInstr[i], instrCenterX, y + stepBase + stepLineH * i, { align: "center" });
        }
      }
      doc.setFontSize(TABLE_FONT);
      doc.setTextColor(26, 20, 19);
      for (let i = 0; i < sFreqLines.length; i++) {
        doc.text(sFreqLines[i], freqCenterX, y + stepBase + stepLineH * i, { align: "center" });
      }
      doc.text(`${step.days} days`, cxDays + cellPad, y + stepBase);
      y += stepH;
    }

    return y;
  };

  for (let i = 0; i < data.medicines.length; i++) {
    y = await drawMedicineRow(data.medicines[i], i, y, pageTopRef);
    // Record the bottom of this row as a horizontal separator boundary.
    rowBoundaries.push(y);
  }

  // Subtle vertical column separators (light gray, 0.3pt) for the final
  // page segment — from the current page's table top to the bottom of the
  // last medicine row. (Separators for earlier pages were already drawn by
  // onPageBreak before each page break.) Drawn at the 4 column boundaries:
  // No.|Drug, Drug|Instructions, Instructions|Frequency, Frequency|Days.
  drawSeparators(pageTopRef.val, y);

  // One full blank row (double-spaced pitch) after the last medicine row.
  y += lineH;

  // Signatory block (bottom-right, anchored above the footer).
  const signY = getSignatureLineY(doc);
  const signX = x + w - mmToPt(60);

  // Review line — bold, 12pt, bottom-left, parallel to the doctor name
  // (same Y as the doctor name line at signY + 4mm). At least one row
  // of space from the footer (signY + 4mm leaves 8mm above the footer top
  // at signY + 12mm — well over one 8.5mm row). If the table is long
  // enough that y has already passed that point, the review line stays
  // right after the table instead of overlapping it.
  const reviewY = Math.max(y, signY + mmToPt(4));
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.setTextColor(26, 20, 19);
  if (data.reviewNeeded) {
    // PRN: review only if the patient needs it.
    doc.text("Review in case of any new symptoms or if required", x, reviewY);
  } else if (data.reviewDate) {
    doc.text(`Review on: ${fmtDatePdf(data.reviewDate)}`, x, reviewY);
  } else {
    doc.text("No review needed", x, reviewY);
  }

  // Signature rule + doctor name / qualifications / reg no (right-aligned).
  doc.setDrawColor(200, 190, 180);
  doc.line(signX, signY, signX + mmToPt(50), signY);
  doc.setFontSize(10);
  doc.setFont("helvetica", "bold");
  doc.text(data.doctorName ?? "Doctor", signX + mmToPt(50), signY + mmToPt(4), { align: "right" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(107, 95, 88);
  if (data.doctorQualifications) {
    doc.text(data.doctorQualifications, signX + mmToPt(50), signY + mmToPt(7), { align: "right" });
  }
  if (data.doctorRegNumber) {
    doc.text(`Reg No: ${data.doctorRegNumber}`, signX + mmToPt(50), signY + mmToPt(10), { align: "right" });
  }

  return doc;
}
